/** Moderation: cases, decisions and blocks (admins). */
import { baseApi } from '../baseApi';
import type {
  CaseIn,
  CasePage,
  ModeratedUserPage,
  ModerationCase,
  ModerationTargetKind,
} from '../types';

export const moderationApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listCases: build.query<
      CasePage,
      { status?: 'open' | 'resolved' | undefined; cursor?: string | undefined }
    >({
      query: (params) => ({ url: '/moderation/cases', params }),
      providesTags: ['Moderation'],
    }),
    // Any admin may hand something over; an open case about it is returned as is.
    openCase: build.mutation<ModerationCase, CaseIn>({
      query: (body) => ({ url: '/moderation/cases', method: 'POST', body }),
      invalidatesTags: ['Moderation'],
    }),
    decideCase: build.mutation<
      ModerationCase,
      { id: string; decision: 'block' | 'dismiss'; note?: string | undefined }
    >({
      query: ({ id, ...body }) => ({
        url: `/moderation/cases/${id}/decision`,
        method: 'POST',
        body,
      }),
      // A block or its end changes what the hub and the courses show.
      invalidatesTags: ['Moderation', 'Content'],
    }),
    liftBlock: build.mutation<
      ModerationCase,
      { kind: ModerationTargetKind; id: string; note?: string | undefined }
    >({
      query: ({ kind, id, note }) => ({
        url: `/moderation/blocks/${kind}/${id}/lift`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: ['Moderation', 'Content'],
    }),
    listReportableUsers: build.query<
      ModeratedUserPage,
      { q?: string | undefined; cursor?: string | undefined }
    >({
      query: (params) => ({ url: '/moderation/users', params }),
      providesTags: ['Moderation'],
    }),
  }),
});

export const {
  useListCasesQuery,
  useOpenCaseMutation,
  useDecideCaseMutation,
  useLiftBlockMutation,
  useListReportableUsersQuery,
} = moderationApi;
